import type { ReactNode } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { titleForPath } from '@/personas/admin/pages/titleForPath';

interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}

/**
 * שורת פתיחה של מסך. בעיצוב v2 (28/09/2026) שם המסך מופיע רק בסרגל העליון,
 * אז כותרת שזהה לשם המסך נשארת לקוראי מסך בלבד, והשורה הופכת לסרגל כלים
 * של הפעולות בלבד (ובלי פעולות — לא תופסת מקום). כותרת שונה משם המסך
 * (למשל קטע בתוך לשונית) מוצגת כרגיל.
 */
export function PageHeader(props: PageHeaderProps) {
  // מחוץ ל-Router (טסטים שמרנדרים מסך בודד) אין נתיב — מציגים את הכותרת.
  return useInRouterContext() ? <RoutedPageHeader {...props} /> : <PageHeaderView {...props} duplicate={false} />;
}

function RoutedPageHeader(props: PageHeaderProps) {
  const { pathname } = useLocation();
  const duplicate = typeof props.title === 'string' && pathname.startsWith('/admin') && titleForPath(pathname) === props.title;
  return <PageHeaderView {...props} duplicate={duplicate} />;
}

function PageHeaderView({ title, subtitle, actions, duplicate }: PageHeaderProps & { duplicate: boolean }) {
  if (duplicate) {
    return (
      <>
        <h2 className="sr-only">{title}</h2>
        {actions && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>{actions}</div>
        )}
      </>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--gap)',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <h2 style={{ margin: 0, fontSize: 'var(--t-xl)', fontWeight: 700, letterSpacing: -0.2, lineHeight: 1.25 }}>{title}</h2>
        {subtitle && (
          <p style={{ margin: '3px 0 0', color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>{subtitle}</p>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginInlineStart: 'auto' }}>{actions}</div>}
    </div>
  );
}
